export type PianoLoadState = 'loading' | 'loaded' | 'error';

export interface PianoLoadStateMachine {
  getState: () => PianoLoadState;
  markLoaded: () => void;
  markError: () => void;
  retry: () => void;
  dispose: () => void;
}

export interface CreatePianoLoadStateMachineOptions {
  timeoutMs: number;
  onStateChange?: (state: PianoLoadState) => void;
}

export function createPianoLoadStateMachine(
  options: CreatePianoLoadStateMachineOptions
): PianoLoadStateMachine {
  const { timeoutMs, onStateChange } = options;
  let state: PianoLoadState = 'loading';
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const clearPendingTimeout = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const setState = (next: PianoLoadState) => {
    if (disposed) return;
    state = next;
    onStateChange?.(next);
  };

  const armTimeout = () => {
    clearPendingTimeout();
    timeoutId = setTimeout(() => {
      timeoutId = null;
      if (state === 'loading') {
        setState('error');
      }
    }, timeoutMs);
  };

  armTimeout();

  return {
    getState: () => state,
    markLoaded: () => {
      clearPendingTimeout();
      setState('loaded');
    },
    markError: () => {
      clearPendingTimeout();
      setState('error');
    },
    retry: () => {
      setState('loading');
      armTimeout();
    },
    dispose: () => {
      disposed = true;
      clearPendingTimeout();
    },
  };
}
